import React, { useState, useEffect, createContext, useContext } from "react";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Box,
  Paper,
  CssBaseline,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { GlobalStyles } from "@mui/material";
import safeLogger from "../utils/safeLogger";

// Criação do contexto para o tema
const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

const GeneralConfig = () => {
  const themeContext = useThemeContext();

  // Fallback para evitar crashes caso o contexto não esteja disponível
  if (!themeContext) {
    safeLogger.warn("ThemeContext não está disponível.");
    return (
      <Container>
        <Typography variant="h6" color="error">
          Erro ao carregar configurações. Tente novamente mais tarde.
        </Typography>
      </Container>
    );
  }

  const { darkMode, toggleTheme } = themeContext;

  safeLogger.info("Configurações Gerais - Tema atual:", darkMode ? "Escuro" : "Claro");

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [language, setLanguage] = useState("pt-BR");

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
    localStorage.setItem("language", event.target.value);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Configurações Gerais
      </Typography>
      <Paper
        elevation={2}
        sx={{
          padding: "16px",
          marginBottom: "24px",
          marginTop: "16px",
          backgroundColor: darkMode ? "#1d1d1d" : "#f9f9f9",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Preferências de Tema
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginBottom: "16px" }}
        >
          <Typography>Modo Escuro</Typography>
          <Switch checked={darkMode} onChange={toggleTheme} />
        </Box>
        <Typography variant="h6" gutterBottom>
          Idioma
        </Typography>
        <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
          <InputLabel
            id="language-select-label"
            sx={{
              backgroundColor: darkMode ? "#1d1d1d" : "#ffffff",
              padding: "0 4px",
            }}
          >
            Idioma
          </InputLabel>
          <Select
            labelId="language-select-label"
            value={language}
            onChange={handleLanguageChange}
          >
            <MenuItem value="pt-BR">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/0/05/Flag_of_Brazil.svg"
                alt="Brasil"
                style={{ width: "20px", marginRight: "8px" }}
              />
              Português
            </MenuItem>
            <MenuItem value="en-US">
              <img
                src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg"
                alt="EUA"
                style={{ width: "20px", marginRight: "8px" }}
              />
              Inglês
            </MenuItem>
          </Select>
        </FormControl>
      </Paper>
    </Container>
  );
};

export const ThemeProviderWrapper = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("darkMode") === "true"
  );

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      localStorage.setItem("darkMode", !prevMode);
      return !prevMode;
    });
  };

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: { main: "#1d4f91" },
      secondary: { main: "#df4661" },
      background: {
        default: darkMode ? "#121212" : "#ffffff", // Fundo principal
        paper: darkMode ? "#1d1d1d" : "#f9f9f9", // Fundo de componentes
      },
      text: {
        primary: darkMode ? "#ffffff" : "#000000", // Texto principal
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            body: {
              backgroundColor: theme.palette.background.default,
              // color: theme.palette.text.primary,
              // margin: 0,
              // padding: 0,
              // transition: "all 0.3s ease-in-out",
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default GeneralConfig;
